import { useCallback, useEffect, useState } from 'react';

type MediaRefs = {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  audioRef: React.RefObject<HTMLAudioElement | null>;
};

export default function useMediaControls({ videoRef, audioRef }: MediaRefs) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  const updateFromMedia = useCallback(() => {
    const a = audioRef.current;
    const v = videoRef.current;
    if (a) {
      setCurrentTime(a.currentTime || 0);
      setDuration(a.duration || 0);
      setPlaying(!a.paused);
    } else if (v) {
      setCurrentTime(v.currentTime || 0);
      setDuration(v.duration || 0);
      setPlaying(!v.paused);
    }
  }, [audioRef, videoRef]);

  useEffect(() => {
    const a = audioRef.current;
    const v = videoRef.current;
    const onTime = () => updateFromMedia();
    const onPlayPause = () => updateFromMedia();
    if (a) {
      a.addEventListener('timeupdate', onTime);
      a.addEventListener('play', onPlayPause);
      a.addEventListener('pause', onPlayPause);
    }
    if (v) {
      v.addEventListener('timeupdate', onTime);
      v.addEventListener('play', onPlayPause);
      v.addEventListener('pause', onPlayPause);
    }
    return () => {
      if (a) {
        a.removeEventListener('timeupdate', onTime);
        a.removeEventListener('play', onPlayPause);
        a.removeEventListener('pause', onPlayPause);
      }
      if (v) {
        v.removeEventListener('timeupdate', onTime);
        v.removeEventListener('play', onPlayPause);
        v.removeEventListener('pause', onPlayPause);
      }
    };
  }, [audioRef.current, videoRef.current, updateFromMedia]);

  const play = useCallback(async () => {
    try {
      const a = audioRef.current;
      const v = videoRef.current;
      if (a) {
        await a.play();
        setPlaying(true);
        return;
      }
      if (v) {
        await v.play();
        setPlaying(true);
      }
    } catch (e) {
      // ignore
    }
  }, [audioRef, videoRef]);

  const pause = useCallback(() => {
    const a = audioRef.current;
    const v = videoRef.current;
    if (a) {
      a.pause();
      setPlaying(false);
      return;
    }
    if (v) {
      v.pause();
      setPlaying(false);
    }
  }, [audioRef, videoRef]);

  const seekTo = useCallback((s?: number) => {
    if (typeof s !== 'number' || Number.isNaN(s)) return;
    const a = audioRef.current;
    const v = videoRef.current;
    try {
      if (a) {
        a.currentTime = Math.max(0, s);
        a.play().catch(() => undefined);
        return;
      }
      if (v) {
        v.currentTime = Math.max(0, s);
        v.play().catch(() => undefined);
      }
    } catch (e) {
      // ignore
    }
  }, [audioRef, videoRef]);

  const skip = useCallback((secs: number) => {
    const t = currentTime || 0;
    seekTo(Math.max(0, Math.min((duration || 0), t + secs)));
  }, [currentTime, duration, seekTo]);

  return {
    play,
    pause,
    seekTo,
    skip,
    currentTime,
    duration,
    playing,
  } as const;
}
